import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, BellRing, FileArchive, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTakeoff } from "./store";
import { StatusBadge, BTN_ORANGE, INPUT, CARD } from "./theme";
import { ConfirmDialog, EditProjectDialog, NotesDialog } from "./dialogs";
import { getNoteVersions, ackNoteVersion } from "./api";
import NoteAlertDialog from "./NoteAlertDialog";
import ImportJobDialog from "./ImportJobDialog";

const SORT_KEY = "takeoff-jobs-due-sort"; // "asc" | "desc" | "" (created order)

/**
 * Home > Projects — job table: search, status filter, due-date sorting
 * (clickable column, missing dates always last, stable), reset, result
 * count, row status changes, edit/delete. No estimate prices here
 * (spec: the Projects list deliberately hides money).
 */
export default function ProjectsTable({ onOpenProject, onNewJob }) {
  const { workspace, statuses, updateProject, deleteProject, loadWorkspace, viewer } = useTakeoff();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dueSort, setDueSortState] = useState(() => {
    try { return localStorage.getItem(SORT_KEY) || ""; } catch { return ""; }
  });
  const [editProj, setEditProj] = useState(null);
  const [delProj, setDelProj] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [notesProj, setNotesProj] = useState(null);
  const [noteDlg, setNoteDlg] = useState(null); // {projectId, projectName, versions}

  // Manual open of a NEW NOTE badge (works for both MUST READ and READ) —
  // shows every version still inside the 7-day window.
  const openNoteAlert = async (p) => {
    try {
      const d = await getNoteVersions(p.project_id);
      if (!d.versions?.length) {
        toast.info("That note alert has expired");
        loadWorkspace();
        return;
      }
      setNoteDlg({ projectId: p.project_id, projectName: p.name, versions: d.versions });
    } catch {
      toast.error("Could not load the note");
    }
  };

  const ackFromBadge = async (versionId) => {
    if (!noteDlg) return;
    await ackNoteVersion(noteDlg.projectId, versionId);
    const versions = noteDlg.versions.map((v) => (v.version_id === versionId ? { ...v, read: true } : v));
    if (versions.every((v) => v.read)) {
      setNoteDlg(null);
      loadWorkspace();
    } else {
      setNoteDlg({ ...noteDlg, versions });
    }
  };

  const setDueSort = (v) => {
    setDueSortState(v);
    try { localStorage.setItem(SORT_KEY, v); } catch { /* private mode */ }
  };

  const projects = useMemo(() => {
    let list = [...(workspace?.projects || [])];
    if (q.trim()) list = list.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()));
    if (status) list = list.filter((p) => (p.status || "Not Set") === status);
    // stable base order (newest first) so equal dates keep a fixed order
    list.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
    if (dueSort) {
      const dir = dueSort === "asc" ? 1 : -1;
      list.sort((a, b) => {
        const da = a.due_date ? Date.parse(a.due_date) : null;
        const dbb = b.due_date ? Date.parse(b.due_date) : null;
        if (da == null && dbb == null) return 0;
        if (da == null) return 1;   // no due date always sinks to the bottom
        if (dbb == null) return 1;
        return (da - dbb) * dir;
      });
    }
    return list;
  }, [workspace, q, status, dueSort]);

  return (
    <div className={CARD} data-testid="takeoff-projects-table">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 p-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project names" className={`${INPUT} max-w-xs`} data-testid="takeoff-projects-search" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${INPUT} max-w-[220px]`} data-testid="takeoff-projects-status-filter" aria-label="Filter by status">
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={dueSort} onChange={(e) => setDueSort(e.target.value)} className={`${INPUT} max-w-[200px]`} data-testid="takeoff-projects-due-sort" aria-label="Sort by due date">
          <option value="">Due date: default</option>
          <option value="asc">Due date: earliest first</option>
          <option value="desc">Due date: latest first</option>
        </select>
        <button onClick={() => { setQ(""); setStatus(""); setDueSort(""); }} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-50" data-testid="takeoff-projects-reset">
          <RotateCcw className="h-3.5 w-3.5" /> Reset
        </button>
        <span className="text-xs text-slate-500" data-testid="takeoff-projects-count">{projects.length} job{projects.length === 1 ? "" : "s"}</span>
        {!viewer && <button onClick={() => setImportOpen(true)} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="takeoff-import-backup-btn"><FileArchive className="h-3.5 w-3.5" /> Import Job Backup</button>}
        {!viewer && <button className={BTN_ORANGE} onClick={onNewJob} data-testid="takeoff-new-project-btn"><Plus className="h-3.5 w-3.5" /> New Project</button>}
      </div>

      <ImportJobDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={async (pid) => { await loadWorkspace(); onOpenProject(pid); }}
      />

      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2.5">Project name</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">
                <button type="button"
                  onClick={() => setDueSort(dueSort === "asc" ? "desc" : "asc")}
                  className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-slate-600"
                  title="Sort by due date"
                  data-testid="takeoff-projects-due-header">
                  Due date
                  {dueSort === "asc" ? <ArrowUp className="h-3 w-3 text-[#f97316]" />
                    : dueSort === "desc" ? <ArrowDown className="h-3 w-3 text-[#f97316]" />
                    : <ArrowUpDown className="h-3 w-3 text-slate-300" />}
                </button>
              </th>
              <th className="px-3 py-2.5">Notes</th>
              <th className="px-3 py-2.5 text-center">Drawings</th>
              <th className="px-3 py-2.5 text-center">Takeoffs</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.project_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`takeoff-project-row-${p.project_id}`}>
                <td className="px-4 py-2.5">
                  <button onClick={() => onOpenProject(p.project_id)} className="font-semibold text-slate-900 hover:text-[#f97316]" data-testid={`takeoff-open-project-${p.project_id}`}>
                    {p.name}
                  </button>
                  {p.note_alert && (
                    <button type="button" onClick={() => openNoteAlert(p)}
                      className={`mt-0.5 flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest transition-colors ${
                        p.note_alert.unread > 0
                          ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                      title={p.note_alert.unread > 0
                        ? `Unread note by ${p.note_alert.latest_author || "a teammate"} — tap to read`
                        : "You've read the latest note — tap to view it again"}
                      data-testid={`note-alert-badge-${p.project_id}`}>
                      <BellRing className="h-3 w-3" />
                      {p.note_alert.unread > 0 ? "New note — Must read" : "New note — Read"}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={p.status} />
                    {!viewer && (
                    <select
                      value={p.status || "Not Set"}
                      onChange={(e) => updateProject(p.project_id, { status: e.target.value })}
                      aria-label={`Change status of ${p.name}`}
                      data-testid={`takeoff-row-status-${p.project_id}`}
                      className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-[11px] text-slate-600 focus:border-[#f97316] focus:outline-none"
                    >
                      {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-slate-600">{p.due_date || "—"}</td>
                <td className="max-w-[240px] px-3 py-2.5">
                  <button
                    onClick={() => setNotesProj(p)}
                    title={p.notes || "Add notes"}
                    aria-label={p.notes ? `Read and edit notes for ${p.name}` : `Add notes for ${p.name}`}
                    className="block w-full truncate rounded text-left text-slate-500 hover:text-[#f97316] focus:outline-none focus:ring-1 focus:ring-[#f97316]"
                    data-testid={`takeoff-notes-${p.project_id}`}
                  >
                    {p.notes || <span className="italic text-slate-500">Add notes</span>}
                  </button>
                </td>
                <td className="px-3 py-2.5 text-center font-mono text-slate-700">{p.drawing_count ?? 0}</td>
                <td className="px-3 py-2.5 text-center font-mono text-slate-700">{p.takeoff_count ?? 0}</td>
                <td className="px-3 py-2.5">
                  {!viewer && (
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEditProj(p)} aria-label={`Edit ${p.name}`} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`takeoff-edit-project-${p.project_id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setDelProj(p)} aria-label={`Delete ${p.name}`} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`takeoff-delete-project-${p.project_id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  )}
                </td>
              </tr>
            ))}
            {!projects.length && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">No jobs match — adjust the search or create a new project.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <EditProjectDialog open={!!editProj} onClose={() => setEditProj(null)} project={editProj}
        onSave={(p) => updateProject(editProj.project_id, p)} />
      <NotesDialog open={!!notesProj} onClose={() => setNotesProj(null)} project={notesProj}
        onSave={(notes) => updateProject(notesProj.project_id, { notes })} />
      <NoteAlertDialog open={!!noteDlg} onClose={() => setNoteDlg(null)}
        projectName={noteDlg?.projectName} versions={noteDlg?.versions || []} onAck={ackFromBadge} />
      <ConfirmDialog open={!!delProj} onClose={() => setDelProj(null)}
        title={`Delete ${delProj?.name}?`}
        body="This permanently removes the job's folders, files, takeoffs, labels, notes, manual estimate rows and estimate overrides."
        onConfirm={() => deleteProject(delProj.project_id)} />
    </div>
  );
}
